import ContactFollowup, { WaitingTime } from "@/components/ContactFollowup";
import FollowupSummary from "@/components/FollowupSummary";
import React, { useEffect, useRef, useState } from "react";
import ManageConsultant from "@/components/ManageConsultant";
import InternalNotes from "@/components/InternalNotes";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Users, Wifi, AlertTriangle, Inbox, User } from "lucide-react";
import { SUP } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_CLS = {
  connected:    "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  qr_pending:   "bg-amber-500/10 text-amber-400 border-amber-500/30",
  disconnected: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  banned:       "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

function formatClientPhone(value) {
  const n = String(value || "").replace(/\D/g, "");
  if (n.startsWith("55") && (n.length === 12 || n.length === 13)) {
    const local = n.slice(4);
    return `+55 (${n.slice(2, 4)}) ${local.slice(0, -4)}-${local.slice(-4)}`;
  }
  return n ? `+${n}` : "";
}

const supervisorPhotoJobs = [];
let supervisorPhotoActive = 0;
function runSupervisorPhotoJobs() {
  while (supervisorPhotoActive < 2 && supervisorPhotoJobs.length) {
    const job = supervisorPhotoJobs.shift();
    supervisorPhotoActive++;
    Promise.resolve().then(job).catch(() => {}).finally(() => {
      supervisorPhotoActive--;
      runSupervisorPhotoJobs();
    });
  }
}

function SupervisorClientAvatar({ userId, phone, sendPhone }) {
  const holder = useRef(null);
  const [photo, setPhoto] = useState(null);
  useEffect(() => {
    let active = true, started = false;
    const controller = new AbortController();
    const start = () => {
      if (!active || started) return;
      started = true;
      supervisorPhotoJobs.push(async () => {
        if (!active) return;
        try {
          const { data } = await api.get(`/supervisor/users/${userId}/contacts/profile-photo`, {
            params: { phone }, timeout: 15000, signal: controller.signal,
          });
          if (active && typeof data?.url === "string" && data.url.startsWith("https://"))
            setPhoto(data.url);
        } catch { /* Keep the contact placeholder when the photo is unavailable. */ }
      });
      runSupervisorPhotoJobs();
    };
    let observer;
    if (holder.current && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) { start(); observer.disconnect(); }
      }, { rootMargin: "80px" });
      observer.observe(holder.current);
    } else start();
    return () => { active = false; controller.abort(); observer?.disconnect(); };
  }, [userId, phone, sendPhone]);
  return <span ref={holder} className="w-9 h-9 rounded-full overflow-hidden bg-slate-800 grid place-items-center flex-none">
    {photo ? <img src={photo} alt="Foto do cliente" referrerPolicy="no-referrer"
      className="w-full h-full object-cover" onError={() => setPhoto(null)} />
      : <User className="w-4 h-4 text-pink-400" />}
  </span>;
}

function DrillDown({ user }) {
  const [contacts, setContacts] = useState([]);
  const [conversationTab, setConversationTab] = useState("messages");
  const [phone, setPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contactsError, setContactsError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [editingContact, setEditingContact] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [savingContact, setSavingContact] = useState(false);
  const contactSaveLock = useRef(false);
  const contactEpoch = useRef(0);
  const bottom = useRef(null);
  const sendLock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const epoch = contactEpoch.current;
        const { data } = await api.get(`/supervisor/users/${user.id}/conversations`);
        if (active && epoch === contactEpoch.current && !contactSaveLock.current) { setContacts(data); setContactsError(""); }
      } catch { if (active) setContactsError("Não foi possível carregar os contatos."); }
    };
    load(); const timer = setInterval(load, 10000);
    return () => { active = false; clearInterval(timer); };
  }, [user.id]);

  useEffect(() => {
    if (!phone) return;
    let active = true;
    let busy = false;
    setLoading(true); setMessages([]); setError("");
    const load = async () => {
      if (busy) return;
      busy = true;
      try {
        const { data } = await api.get(`/supervisor/users/${user.id}/messages`, { params: { phone } });
        if (active) { setMessages(data); setError(""); }
      } catch { if (active) setError("Não foi possível atualizar as mensagens."); }
      finally { busy = false; if (active) setLoading(false); }
    };
    load(); const timer = setInterval(load, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [phone, user.id, refresh]);

  const lastMessage = messages[messages.length - 1]?.id;
  useEffect(() => { bottom.current?.scrollIntoView({ block: "end" }); }, [phone, lastMessage]);
  const selected = contacts.find(c => c.phone === phone);
  const validPhone = /^\d{10,15}$/.test(selected?.send_phone || phone || "");
  const saveContact = async event => {
    event.preventDefault();
    if (contactSaveLock.current || sending || !selected) return;
    const number = contactNumber.replace(/[^0-9]/g, "");
    if (!contactName.trim() || !/^[1-9][0-9]{9,14}$/.test(number)) {
      toast.error("Informe o nome e o telefone completo com país e DDD");
      return;
    }
    contactSaveLock.current = true;
    contactEpoch.current++;
    setSavingContact(true);
    try {
      const { data } = await api.put(`/supervisor/users/${user.id}/contacts/details`, {
        phone, name: contactName.trim(), send_phone: number,
      });
      if (mounted.current) {
        setContacts(current => current.map(c => c.phone === phone ? { ...c, name: data.name, send_phone: data.send_phone } : c));
        setEditingContact(false);
        toast.success("Contato atualizado");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível salvar o contato");
    } finally {
      contactEpoch.current++;
      contactSaveLock.current = false;
      if (mounted.current) setSavingContact(false);
    }
  };
  const send = async event => {
    event.preventDefault();
    if (sendLock.current || contactSaveLock.current || editingContact || !draft.trim() || !validPhone) return;
    sendLock.current = true; setSending(true);
    try {
      await api.post(`/supervisor/users/${user.id}/send`, { to: phone, text: draft.trim() });
      toast.success("Mensagem enviada pelo WhatsApp da consultora");
      if (mounted.current) { setDraft(""); setRefresh(n => n + 1); }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha no envio. Confira a conversa antes de tentar novamente.");
    } finally {
      sendLock.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 h-[65vh] min-h-0">
      <div className="md:col-span-4 overflow-y-auto space-y-1 pr-2 min-h-0 max-h-[20vh] md:max-h-none">
        <div className="text-xs text-slate-400 mb-2">Contatos ({contacts.length})</div>
        {contactsError && <p role="alert" className="text-rose-400 text-sm">{contactsError}</p>}
        {contacts.map(c => (
          <button key={c.id} disabled={sending || savingContact} onClick={() => { setPhone(c.phone); setDraft(""); setEditingContact(false); setConversationTab("messages"); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm disabled:opacity-50 ${phone === c.phone ? "bg-emerald-500/10 text-emerald-400" : "hover:bg-slate-800/60"}`}>
            <div className="flex items-center gap-2 min-w-0">
              <SupervisorClientAvatar key={`${user.id}:${c.phone}:${c.send_phone || ""}`} userId={user.id} phone={c.phone} sendPhone={c.send_phone} />
              <div className="min-w-0">
                <div className="truncate">{c.name && c.name !== c.phone ? c.name : formatClientPhone(c.send_phone || c.phone)}</div>
                <div className="text-[10px] text-slate-500 truncate">{formatClientPhone(c.send_phone || c.phone)}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="md:col-span-8 flex flex-col border-l border-slate-800 pl-4 min-h-0">
        {phone ? <>
          <div className="flex items-center justify-between gap-2 border-b border-slate-800 pb-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <SupervisorClientAvatar key={`${user.id}:${phone}:${selected?.send_phone || ""}`} userId={user.id} phone={phone} sendPhone={selected?.send_phone} />
              <div className="min-w-0 text-sm">
                <div className="font-medium truncate">{selected?.name && selected.name !== phone ? selected.name : formatClientPhone(selected?.send_phone || phone)}</div>
                <div className="text-xs text-slate-400 truncate">{formatClientPhone(selected?.send_phone || phone)}</div>
              </div>
            </div>
            <Button type="button" size="sm" variant="outline" disabled={sending || savingContact || !selected}
              onClick={() => { setContactName(selected?.name || ""); setContactNumber(selected?.send_phone || phone); setEditingContact(true); }}>Editar contato</Button>
          </div>
          {editingContact && <form onSubmit={saveContact} className="p-3 mb-3 bg-slate-900 border border-slate-700 rounded-lg space-y-2">
            <label className="block text-xs">Nome do cliente
              <input required maxLength={120} value={contactName} disabled={savingContact}
                onChange={e => setContactName(e.target.value)} className="block w-full mt-1 bg-slate-800 border border-slate-600 rounded px-2 py-2 text-sm" />
            </label>
            <label className="block text-xs">Telefone para envio (país + DDD + número)
              <input required inputMode="tel" maxLength={30} value={contactNumber} disabled={savingContact}
                onChange={e => setContactNumber(e.target.value)} className="block w-full mt-1 bg-slate-800 border border-slate-600 rounded px-2 py-2 text-sm" />
            </label>
            <p className="text-xs text-slate-400">Este telefone será usado nas respostas enviadas por esta janela. O histórico e as notas continuam nesta conversa.</p>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={savingContact}>{savingContact ? "Salvando..." : "Salvar contato"}</Button>
              <Button type="button" size="sm" variant="outline" disabled={savingContact} onClick={() => setEditingContact(false)}>Cancelar</Button>
            </div>
          </form>}
          {selected && <><WaitingTime contact={selected} /><ContactFollowup key={`${user.id}:${phone}`} userId={user.id} contact={selected} onSaved={values => {
            contactEpoch.current += 1;
            setContacts(items => items.map(item => item.phone === phone ? { ...item, ...values } : item));
          }} /></>}
          <div className="flex gap-2 mb-2" aria-label="Abas da conversa">
            <Button type="button" size="sm" variant={conversationTab === "messages" ? "default" : "outline"} onClick={() => setConversationTab("messages")} disabled={sending}>Mensagens</Button>
            <Button type="button" size="sm" variant={conversationTab === "notes" ? "default" : "outline"} onClick={() => setConversationTab("notes")} disabled={sending}>Notas internas</Button>
          </div>
          {conversationTab === "notes" ? <InternalNotes key={`${user.id}:${phone}`} userId={user.id} phone={phone} /> : <>
          <div className="flex-1 overflow-y-auto min-h-0 pr-2">
            {loading && <p className="text-slate-400 text-sm">Carregando mensagens...</p>}
            {error && <p role="alert" className="text-rose-400 text-sm">{error}</p>}
            {messages.filter(m => m.text && m.text !== "[text]").map(m => (
              <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"} mb-2`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.direction === "out" ? "bg-emerald-600 text-white" : "bg-slate-800 text-slate-100"}`}>
                  {m.sent_by_name && <div className="text-xs font-semibold mb-1">Enviada por {m.sent_by_name}</div>}
                  {m.text}
                </div>
              </div>
            ))}
            {!loading && !error && messages.length === 0 && <p className="text-slate-400 text-sm">Sem mensagens.</p>}
            <div ref={bottom} />
          </div>
          <form onSubmit={send} className="pt-3 mt-2 border-t border-slate-800 space-y-2">
            <p className="text-xs text-slate-400">Enviar pelo WhatsApp de {user.name || user.email}. A autoria fica registrada no sistema.</p>
            {!validPhone && <p className="text-amber-400 text-xs">Selecione um contato com telefone válido para responder.</p>}
            <textarea aria-label="Mensagem para o cliente" placeholder="Digite sua mensagem..." maxLength={10000} rows={2}
              value={draft} disabled={sending || savingContact || editingContact || !validPhone} onChange={e => setDraft(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm resize-none" />
            <Button type="submit" disabled={sending || savingContact || editingContact || !validPhone || !draft.trim()}>{sending ? "Enviando..." : "Enviar mensagem"}</Button>
          </form>
          </>}
        </> : <p className="text-slate-400 text-sm">Selecione um contato para acompanhar e responder.</p>}
      </div>
    </div>
  );
}

function EditUserAccess({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const changeOpen = (next) => {
    if (saving) return;
    setOpen(next);
    if (next) {
      setName(user.name || "");
      setEmail(user.email || "");
      setPassword("");
      setConfirmPassword("");
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (password && new TextEncoder().encode(password).length > 72) {
      toast.error("A senha é muito longa. Use até 72 bytes de texto.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/supervisor/users/${user.id}/access`, {
        name: name.trim(), email: email.trim(), new_password: password || null,
      });
      setOpen(false);
      setPassword("");
      setConfirmPassword("");
      toast.success("Dados atualizados. O usuário precisará entrar novamente.");
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível atualizar o usuário");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline">Editar dados</Button></DialogTrigger>
      <DialogContent className="bg-[#0F172A] border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle>Editar {user.name || user.email}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <label className="block text-sm">Nome
            <input type="text" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)}
              className="block w-full mt-1 bg-slate-900 border border-slate-700 rounded px-3 py-2" />
          </label>
          <label className="block text-sm">E-mail
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="block w-full mt-1 bg-slate-900 border border-slate-700 rounded px-3 py-2" />
          </label>
          <label className="block text-sm">Nova senha (deixe vazia para manter a atual)
            <input type="password" minLength={10} maxLength={128} autoComplete="new-password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full mt-1 bg-slate-900 border border-slate-700 rounded px-3 py-2" />
          </label>
          <label className="block text-sm">Confirme a nova senha
            <input type="password" minLength={10} maxLength={128} autoComplete="new-password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="block w-full mt-1 bg-slate-900 border border-slate-700 rounded px-3 py-2" />
          </label>
          <p className="text-xs text-slate-400">Ao salvar, as sessões atuais desse usuário serão encerradas.</p>
          <Button type="submit" disabled={saving}>{saving ? "Salvando..." : "Salvar alterações"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function SupervisorDashboardPage() {
  const { user: me } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [newConsultant, setNewConsultant] = useState({ name: "", email: "", password: "" });

  const loadAll = async () => {
    try {
      const [s, f] = await Promise.all([api.get("/supervisor/sessions"), api.get("/supervisor/feedbacks")]);
      setSessions(s.data); setFeedbacks(f.data);
      if (me?.role === "admin") {
        const { data } = await api.get("/supervisor/users");
        setAllUsers(data);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { loadAll(); const t = setInterval(loadAll, 15000); return () => clearInterval(t); }, [me]);

  const assign = async (uid, supervisor_id) => {
    try {
      await api.post("/supervisor/assign", { user_id: uid, supervisor_id: supervisor_id === "__none__" ? null : supervisor_id });
      toast.success("Supervisor atualizado");
      loadAll();
    } catch { toast.error("Falha ao atualizar"); }
  };
  const setRole = async (uid, role) => {
    try {
      await api.post("/supervisor/assign", { user_id: uid, role });
      toast.success("Papel atualizado");
      loadAll();
    } catch { toast.error("Falha ao atualizar"); }
  };

  const createConsultant = async (event) => {
    event.preventDefault();
    try {
      await api.post("/supervisor/create-consultant", newConsultant);
      setNewConsultant({ name: "", email: "", password: "" });
      toast.success("Consultora cadastrada. Informe a senha inicial diretamente a ela.");
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Não foi possível cadastrar a consultora"); }
  };

  const connected = sessions.filter(s => s.session_status === "connected").length;
  const banned = sessions.filter(s => s.session_status === "banned").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Supervisor</h1>
        <p className="text-slate-400 mt-2">Visão consolidada dos usuários {me?.role === "admin" ? "de todo o sistema" : "sob sua supervisão"}.</p>
      </div>

      <FollowupSummary />

      <Card className="bg-[#111827] border-slate-800 p-5">
        <h2 className="font-semibold mb-3">Cadastrar consultora</h2>
        <form onSubmit={createConsultant} className="flex flex-wrap gap-3">
          <input aria-label="Nome da consultora" placeholder="Nome" required value={newConsultant.name}
            onChange={(e) => setNewConsultant({ ...newConsultant, name: e.target.value })}
            className="bg-slate-900 border border-slate-700 rounded px-3 py-2" />
          <input aria-label="Email da consultora" type="email" placeholder="Email" required value={newConsultant.email}
            onChange={(e) => setNewConsultant({ ...newConsultant, email: e.target.value })}
            className="bg-slate-900 border border-slate-700 rounded px-3 py-2" />
          <input aria-label="Senha inicial" type="password" placeholder="Senha inicial (6+ caracteres)" minLength={6} required
            value={newConsultant.password} onChange={(e) => setNewConsultant({ ...newConsultant, password: e.target.value })}
            className="bg-slate-900 border border-slate-700 rounded px-3 py-2" />
          <Button type="submit">Cadastrar</Button>
        </form>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-[#111827] border-slate-800 p-4">
          <div className="flex items-center gap-3"><Users className="w-5 h-5 text-slate-400" /><div><div className="text-xs font-mono uppercase text-slate-500">Usuários</div><div className="text-2xl font-semibold">{sessions.length}</div></div></div>
        </Card>
        <Card className="bg-[#111827] border-slate-800 p-4">
          <div className="flex items-center gap-3"><Wifi className="w-5 h-5 text-emerald-400" /><div><div className="text-xs font-mono uppercase text-slate-500">Conectados</div><div className="text-2xl font-semibold">{connected}</div></div></div>
        </Card>
        <Card className="bg-[#111827] border-slate-800 p-4">
          <div className="flex items-center gap-3"><Inbox className="w-5 h-5 text-emerald-400" /><div><div className="text-xs font-mono uppercase text-slate-500">Feedbacks</div><div className="text-2xl font-semibold">{feedbacks.length}</div></div></div>
        </Card>
        <Card className="bg-[#111827] border-slate-800 p-4">
          <div className="flex items-center gap-3"><AlertTriangle className="w-5 h-5 text-rose-400" /><div><div className="text-xs font-mono uppercase text-slate-500">Banidos</div><div className="text-2xl font-semibold">{banned}</div></div></div>
        </Card>
      </div>

      <Card className="bg-[#111827] border-slate-800 p-5">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Usuários & Sessões</div>
        <div className="space-y-2">
          {sessions.map((u) => (
            <div key={u.id} data-testid={SUP.userRow} className="flex items-center justify-between gap-4 p-3 rounded-lg hover:bg-slate-800/40 border border-transparent hover:border-slate-800">
              <div className="flex items-center gap-3 min-w-0">
                {u.profile_photo ? <img src={u.profile_photo} alt={`Foto de ${u.name || u.email}`} className="w-9 h-9 rounded-full object-cover flex-none" /> : <div className="w-9 h-9 rounded-full bg-slate-800 grid place-items-center flex-none"><User className="w-4 h-4 text-slate-400" /></div>}
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name || u.email}</div>
                  <div className="text-xs font-mono text-slate-500 truncate">{u.email} • {u.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`border ${STATUS_CLS[u.session_status]}`}>{u.session_status}</Badge>
                {["user", "consultora", "consultant"].includes(u.role) && u.id !== me?.id && <ManageConsultant user={u} onSaved={loadAll} />}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="border-slate-800 hover:bg-slate-800/60" data-testid={SUP.drilldownButton}>
                      <Eye className="w-4 h-4 mr-1.5" /> Ver conversas
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl bg-[#0F172A] border-slate-800 text-slate-100">
                    <DialogHeader>
                      <DialogTitle>Conversas - {u.name || u.email}</DialogTitle>
                    </DialogHeader>
                    <DrillDown user={u} />
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          ))}
          {sessions.length === 0 && <div className="text-slate-500 text-sm p-6 text-center">Nenhum usuário para exibir.</div>}
        </div>
      </Card>

      {me?.role === "admin" && (
        <Card className="bg-[#111827] border-slate-800 p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Admin â€” Papéis & Hierarquia</div>
          <div className="space-y-2">
            {allUsers.map((u) => {
              const supervisors = allUsers.filter(x => x.role === "supervisor" && x.id !== u.id);
              return (
                <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg hover:bg-slate-800/40 border border-transparent hover:border-slate-800">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.name || u.email}</div>
                    <div className="text-xs font-mono text-slate-500 truncate">{u.email}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    {u.role !== "admin" && <EditUserAccess user={u} onSaved={loadAll} />}
                    <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                      <SelectTrigger className="w-32 bg-slate-900/60 border-slate-800"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-800 text-slate-100">
                        <SelectItem value="user">user</SelectItem>
                        <SelectItem value="supervisor">supervisor</SelectItem>
                        <SelectItem value="admin">admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={u.supervisor_id || "__none__"} onValueChange={(v) => assign(u.id, v)}>
                      <SelectTrigger className="w-52 bg-slate-900/60 border-slate-800"><SelectValue placeholder="Supervisor" /></SelectTrigger>
                      <SelectContent className="bg-[#0F172A] border-slate-800 text-slate-100">
                        <SelectItem value="__none__">â€” sem supervisor â€”</SelectItem>
                        {supervisors.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}


    </div>
  );
}










